import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import {
  Check,
  Shield,
  CreditCard,
  RefreshCw,
  Gift,
  CheckCircle,
  Percent
} from 'lucide-react';

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Referral kód
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(false);
  const [referralChecking, setReferralChecking] = useState(false);
  const [hasExistingDiscount, setHasExistingDiscount] = useState(false);
  const [existingDiscount, setExistingDiscount] = useState(0);
  const [loading, setLoading] = useState(null); // 'planKey' nebo null

  // Zkontrolovat existující slevu (referrer discount)
  useEffect(() => {
    const checkExistingDiscount = async () => {
      if (!user?.id) return;
      try {
        const discount = await api.getCheckoutDiscount(user.id);
        if (discount.discount > 0) {
          setHasExistingDiscount(true);
          setExistingDiscount(discount.discount);
        }
      } catch (error) {
        console.log('No existing discount');
      }
    };
    checkExistingDiscount();
  }, [user?.id]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(false);
      return;
    }
    setReferralChecking(true);
    try {
      const result = await api.validateReferralCode(code);
      setReferralValid(result.valid);
    } catch (error) {
      setReferralValid(false);
    } finally {
      setReferralChecking(false);
    }
  };

  const features = [
    'Simulace přijímaček',
    'Podrobné vysvětlení odpovědí',
    'Statistiky a sledování pokroku',
    'Flashcards pro rychlé učení',
    'Uložení rozpracovaného testu',
  ];

  const plans = [
    {
      key: '1_month',
      name: '1 měsíc',
      price: 249,
      pricePerMonth: 249,
      popular: false,
      gradient: 'from-pink-500 to-rose-600',
    },
    {
      key: '3_months',
      name: '3 měsíce',
      price: 699,
      pricePerMonth: 233,
      popular: false,
      gradient: 'from-emerald-500 to-teal-600',
    },
    {
      key: '6_months',
      name: '6 měsíců',
      price: 1290,
      pricePerMonth: 215,
      popular: true,
      gradient: 'from-indigo-600 via-purple-600 to-indigo-700',
    },
  ];

  const handleSubscribe = async (planKey) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (loading) return; // Prevent double-click
    setLoading(planKey);

    try {
      // Pokud je platný referral kód, nejdřív ho aplikuj
      if (referralValid && referralCode) {
        await api.applyReferralCode(referralCode, user.id);
      }

      const response = await api.createCheckoutSession(planKey, user.id, user.email);

      if (response.url) {
        window.location.href = response.url;
      } else {
        alert('Chyba při vytváření platby. Zkuste to znovu.');
        setLoading(null);
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Chyba při připojení k platební bráně. Zkuste to znovu.');
      setLoading(null);
    }
  };

  const guarantees = [
    {
      icon: Shield,
      title: 'Bezpečné platby',
      description: 'Všechny platby jsou zabezpečeny pomocí Stripe.'
    },
    {
      icon: CreditCard,
      title: 'Přijímáme všechny karty',
      description: 'Visa, Mastercard a další platební metody.'
    },
    {
      icon: RefreshCw,
      title: 'Zrušení kdykoliv',
      description: 'Předplatné můžete kdykoliv zrušit.'
    },
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-20">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-60 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Vyberte si svůj plán
          </h1>
          <p className="text-dark-400 max-w-2xl mx-auto">
            Investice do vzdělání, která se vám mnohonásobně vrátí.
          </p>
        </div>

        {/* Discount Banner */}
        {user && hasExistingDiscount && (
          <div className="max-w-2xl mx-auto mb-8">
            <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Percent size={20} className="text-green-400" />
                </div>
                <div>
                  <p className="text-green-400 font-semibold">Máš aktivní slevu {existingDiscount}%!</p>
                  <p className="text-dark-400 text-sm">Sleva se automaticky aplikuje při platbě</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Referral Code Input - pouze pro přihlášené bez existující slevy */}
        {user && !hasExistingDiscount && (
          <div className="max-w-md mx-auto mb-8">
            <div className="glass rounded-xl p-4">
              <label className="block text-dark-300 text-sm font-medium mb-2">
                Pozval tě kamarád? Zadej kód z pozvánky.
              </label>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <Gift size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-500" />
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => {
                      const code = e.target.value.toUpperCase();
                      setReferralCode(code);
                      if (code.length >= 4) {
                        validateReferralCode(code);
                      } else {
                        setReferralValid(false);
                      }
                    }}
                    placeholder="Zadej kód"
                    className={`w-full pl-10 pr-10 py-2.5 rounded-xl bg-dark-800 border text-white placeholder-dark-500 focus:outline-none transition-colors font-mono tracking-wider ${
                      referralValid ? 'border-green-500' : 'border-dark-700 focus:border-primary-500'
                    }`}
                  />
                  {referralChecking && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                  )}
                  {!referralChecking && referralValid && (
                    <CheckCircle size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-400" />
                  )}
                </div>
              </div>
              {referralValid && (
                <p className="text-green-400 text-sm mt-2">Získáš 10% slevu na první předplatné!</p>
              )}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.key}
                className={`relative rounded-2xl glass p-6 ${plan.popular ? 'ring-2 ring-primary-500' : ''}`}
              >
                {/* Badge for popular plan */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full bg-primary-500 text-white text-xs font-medium whitespace-nowrap">
                      NEJLEPŠÍ VOLBA
                    </span>
                  </div>
                )}

                {/* Header */}
                <div className="text-center mb-6">
                  <p className="text-dark-400 text-sm mb-2">{plan.name}</p>
                  <p className="text-4xl font-bold text-white">{plan.price} Kč</p>
                  <p className="text-dark-500 text-sm mt-1">{plan.pricePerMonth} Kč / měsíc</p>
                </div>

                {/* Button */}
                <button
                  onClick={() => handleSubscribe(plan.key)}
                  disabled={loading !== null}
                  className={`w-full py-3 rounded-xl font-semibold transition-colors mb-6 ${
                    plan.popular
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : 'bg-dark-700 text-white hover:bg-dark-600'
                  } ${loading !== null ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading === plan.key ? 'Načítání...' : 'Předplatit'}
                </button>

                {/* Features list */}
                <div className="space-y-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check size={16} className="text-green-400 flex-shrink-0" />
                      <span className="text-dark-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Guarantees */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {guarantees.map((item, index) => (
            <div key={index} className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <item.icon size={24} className="text-primary-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-dark-400 text-sm">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Teaser */}
        <div className="mt-20 text-center">
          <p className="text-dark-400">
            Máte otázky? Kontaktujte nás na{' '}
            <a href="mailto:info@synapso.cz" className="text-primary-400 hover:underline">
              info@synapso.cz
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
